import React, { ReactNode } from "react";

interface ButtonProps {
  children: React.ReactNode; // Button text or content
  size?: 'sm' | 'md' | 'lg'; // Button size
  variant?: "primary" | "outline" | "danger" | "success" | "purple"; // Button variant
  startIcon?: ReactNode; // Icon before the text
  endIcon?: ReactNode; // Icon after the text
  onClick?: () => void; // Click handler
  disabled?: boolean; // Disabled state
  className?: string; // Additional classes
  htmlType?: 'button' | 'submit' | 'reset';  // Button type
  testId?: string; // Test ID for testing
}

const Button: React.FC<ButtonProps> = ({
  children,
  size = "md",
  variant = "primary",
  startIcon,
  endIcon,
  onClick,
  className = "",
  disabled = false,
  htmlType = 'button',
  testId,
}) => {
  // Size Classes
  const sizeClasses = {
    sm: "px-3 py-[6px] text-sm",
    md: "px-5 py-3.5 text-sm",
    lg: "px-6 py-4 text-sm",
  };

  // Variant Classes
  const variantClasses = {
    primary:
      "bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600 disabled:bg-brand-300",
    success:
      "bg-green-500 text-white shadow-theme-xs hover:bg-green-600 disabled:bg-green-300",
    outline:
      "bg-bg-secondary text-text-secondary ring-1 ring-inset ring-border-primary hover:bg-bg-hover",
    danger: "bg-red-500 text-white hover:bg-red-600",
    purple: "bg-purple-700 text-white hover:bg-purple-800",
  };

  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-lg transition ${className} ${
        sizeClasses[size]
      } ${variantClasses[variant]} ${
        disabled ? "cursor-not-allowed opacity-50" : ""
      }`}
      onClick={onClick}
      disabled={disabled}
      type={htmlType}
      data-testid={testId}
    >
      {startIcon && <span className="flex items-center">{startIcon}</span>}
      {children}
      {endIcon && <span className="flex items-center">{endIcon}</span>}
    </button>
  );
};

export default Button;
